/**
 * Fireworks Visualizer - Audio-reactive particle fireworks simulation
 *
 * Rockets launch on beats, burst into colorful explosions with trailing particles.
 * Uses a pre-allocated object pool (2000 particles) for zero-GC animation.
 * Overrides draw() for semi-transparent trail fade effect.
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'fireworks',
  displayName: 'Fireworks',
  contextType: '2d',
};

// Particle states
const DEAD = 0;
const ROCKET = 1;
const BURST = 2;
const SPARKLE = 3;

// Color palettes — arrays of [R, G, B] tuples
const PALETTES = {
  'gold-red': [
    [255, 215, 0], [255, 191, 0], [255, 100, 0], [255, 50, 50], [255, 240, 200]
  ],
  'blue-silver': [
    [135, 206, 250], [70, 130, 180], [176, 196, 222], [255, 255, 255], [200, 230, 255]
  ],
  'green-gold': [
    [50, 205, 50], [144, 238, 144], [255, 215, 0], [100, 255, 100], [238, 232, 170]
  ],
  'purple-pink': [
    [148, 0, 211], [255, 0, 255], [255, 105, 180], [200, 162, 255], [255, 182, 193]
  ],
  'rainbow': [
    [255, 50, 50], [255, 165, 0], [255, 255, 50], [50, 255, 50], [50, 100, 255], [180, 50, 255]
  ],
  'white-gold': [
    [255, 255, 255], [255, 250, 240], [255, 235, 180], [255, 215, 0], [255, 245, 220]
  ]
};

const MAX_PARTICLES = 2000;
const LAUNCH_COOLDOWN_MS = 100;

class Fireworks extends Base2DVisualizer {
  static name = 'fireworks';
  static displayName = 'Fireworks';

  static getConfigUI() {
    return [
      { type: 'range', id: 'launchIntensity', label: 'Launch Intensity', min: 1, max: 5, default: 2 },
      { type: 'range', id: 'burstSize', label: 'Burst Size', min: 30, max: 200, default: 80 },
      { type: 'range', id: 'burstHeight', label: 'Burst Height', min: 20, max: 90, default: 60, scale: 0.01 },
      { type: 'checkbox', id: 'secondaryBursts', label: 'Secondary Bursts', default: true },
      { type: 'range', id: 'trailFade', label: 'Trail Fade', min: 3, max: 30, default: 8, scale: 0.01 },
      { type: 'range', id: 'gravityScale', label: 'Gravity', min: 2, max: 15, default: 6, scale: 0.01 },
      { type: 'checkbox', id: 'glow', label: 'Glow Effect', default: true },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 70, scale: 0.01 },
      {
        type: 'select', id: 'colorPalette', label: 'Color Palette', default: 'auto',
        options: [
          { value: 'auto', label: 'Auto (Cycle)' },
          { value: 'gold-red', label: 'Gold & Red' },
          { value: 'blue-silver', label: 'Blue & Silver' },
          { value: 'green-gold', label: 'Green & Gold' },
          { value: 'purple-pink', label: 'Purple & Pink' },
          { value: 'rainbow', label: 'Rainbow' },
          { value: 'white-gold', label: 'White & Gold' },
          { value: 'custom', label: 'Custom Colors' }
        ]
      },
      { type: 'color', id: 'colorStart', label: 'Custom Color Start', default: '#ff4400' },
      { type: 'color', id: 'colorEnd', label: 'Custom Color End', default: '#ffdd00' },
      { type: 'color', id: 'backgroundColor', label: 'Background', default: '#050510' }
    ];
  }

  getDefaultConfig() {
    return {
      colorPalette: 'auto',
    };
  }

  init() {
    // Pre-allocate particle pool — fixed object shape for V8 hidden class optimization
    this.particles = new Array(MAX_PARTICLES);
    this.freeList = new Array(MAX_PARTICLES);
    this.freeCount = MAX_PARTICLES;

    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.particles[i] = {
        x: 0, y: 0,
        vx: 0, vy: 0,
        life: 0, alpha: 0,
        size: 0,
        r: 0, g: 0, b: 0,
        gravity: 0, friction: 1,
        state: DEAD,
        burstIndex: 0,
        targetY: 0
      };
      this.freeList[i] = i;
    }

    // Launch state
    this.lastLaunchTime = 0;
    this.ambientEnergy = 0;
    this.paletteIndex = 0;
    this.isFirstFrame = true;

    // Cached fill style to avoid per-particle string allocation
    this._lastR = -1;
    this._lastG = -1;
    this._lastB = -1;
    this._lastFillStyle = '';

    // Pending secondary bursts: { x, y, paletteColors, framesLeft }
    this.pendingBursts = [];

    // Cache parsed background RGB and pre-built trail fill style
    this._bgR = 5;
    this._bgG = 5;
    this._bgB = 16;
    this._trailFillStyle = '';
    this._updateTrailFillStyle();
  }

  onConfigChange(changed) {
    if (changed.backgroundColor !== undefined || changed.trailFade !== undefined) {
      this._updateTrailFillStyle();
    }
  }

  /**
   * Cache the trail overlay fill style string to avoid per-frame string allocation.
   */
  _updateTrailFillStyle() {
    const bg = this.config.backgroundColor || '#050510';
    this._bgR = parseInt(bg.slice(1, 3), 16) || 0;
    this._bgG = parseInt(bg.slice(3, 5), 16) || 0;
    this._bgB = parseInt(bg.slice(5, 7), 16) || 0;
    this._trailFillStyle = `rgba(${this._bgR}, ${this._bgG}, ${this._bgB}, ${this.config.trailFade})`;
  }

  getElementCount() {
    return 8;
  }

  /**
   * Allocate a particle from the pool.
   * @returns {number} Particle index, or -1 if pool exhausted
   */
  allocParticle() {
    if (this.freeCount === 0) return -1;
    return this.freeList[--this.freeCount];
  }

  /**
   * Return a particle to the free pool. Guards against double-free.
   */
  freeParticle(index) {
    if (this.particles[index].state === DEAD) return;
    this.particles[index].state = DEAD;
    this.freeList[this.freeCount++] = index;
  }

  /**
   * Get palette colors for the current burst.
   * In auto mode, cycles through palettes biased by spectral centroid.
   */
  getPaletteColors() {
    const { colorPalette } = this.config;

    if (colorPalette === 'custom') {
      return this._generateCustomPalette();
    }

    if (colorPalette === 'auto') {
      const brightness = this.spectralBrightness;
      // Warm palettes for high spectral centroid, cool for low
      if (brightness > 0.6) {
        const warm = ['gold-red', 'green-gold', 'white-gold', 'rainbow'];
        return PALETTES[warm[this.paletteIndex % warm.length]];
      } else {
        const cool = ['blue-silver', 'purple-pink', 'rainbow'];
        return PALETTES[cool[this.paletteIndex % cool.length]];
      }
    }

    return PALETTES[colorPalette] || PALETTES['gold-red'];
  }

  /**
   * Generate a 5-stop custom palette by interpolating colorStart to colorEnd.
   */
  _generateCustomPalette() {
    const start = this.config.colorStart;
    const end = this.config.colorEnd;
    const r1 = parseInt(start.slice(1, 3), 16);
    const g1 = parseInt(start.slice(3, 5), 16);
    const b1 = parseInt(start.slice(5, 7), 16);
    const r2 = parseInt(end.slice(1, 3), 16);
    const g2 = parseInt(end.slice(3, 5), 16);
    const b2 = parseInt(end.slice(5, 7), 16);

    const palette = [];
    for (let i = 0; i < 5; i++) {
      const t = i / 4;
      palette.push([
        Math.round(r1 + (r2 - r1) * t),
        Math.round(g1 + (g2 - g1) * t),
        Math.round(b1 + (b2 - b1) * t)
      ]);
    }
    return palette;
  }

  /**
   * Launch a single rocket from the bottom of the canvas.
   */
  launchRocket(energy) {
    const idx = this.allocParticle();
    if (idx === -1) return;

    const { canvas, config } = this;
    const p = this.particles[idx];

    // Randomize burst height around the configured center (±30% spread)
    const heightVar = config.burstHeight * (0.7 + Math.random() * 0.6);
    const clampedHeight = Math.max(0.10, Math.min(0.95, heightVar));

    p.x = canvas.width * (0.15 + Math.random() * 0.7);
    p.y = canvas.height;
    p.vx = (Math.random() - 0.5) * 2;
    // Velocity scales with burst height so rockets travel at appropriate speed
    p.vy = -canvas.height * 0.01 * (0.5 + clampedHeight * 0.8) * (0.8 + energy * 0.2);
    p.life = 1;
    p.alpha = 1;
    p.size = 3;
    // Bright white/gold core
    p.r = 255;
    p.g = 240;
    p.b = 200;
    p.gravity = 0.03;
    p.friction = 1;
    p.state = ROCKET;
    p.burstIndex = this.paletteIndex++;
    p.targetY = canvas.height * (1 - clampedHeight);
  }

  /**
   * Burst a rocket into child particles at its current position.
   */
  burstRocket(rocketIdx) {
    const rocket = this.particles[rocketIdx];
    const x = rocket.x;
    const y = rocket.y;
    const paletteColors = this.getPaletteColors();
    const burstCount = Math.min(
      this.config.burstSize,
      Math.floor(60 + this.bassEnergy * 90)
    );

    this._spawnBurst(x, y, paletteColors, burstCount, false);

    // 30% chance of scheduling a delayed secondary burst (capped at 50)
    if (this.config.secondaryBursts && Math.random() < 0.3 && this.pendingBursts.length < 50) {
      this.pendingBursts.push({
        x: x + (Math.random() - 0.5) * 30,
        y: y + (Math.random() - 0.5) * 30,
        paletteColors,
        framesLeft: 15 + Math.floor(Math.random() * 20)
      });
    }

    this.freeParticle(rocketIdx);
  }

  /**
   * Spawn burst particles at a position in a circular spread.
   */
  _spawnBurst(x, y, paletteColors, count, isSecondary) {
    const angleStep = (Math.PI * 2) / count;
    const baseSpeed = isSecondary ? 2 : 4;
    const gravity = this.config.gravityScale;

    for (let i = 0; i < count; i++) {
      const idx = this.allocParticle();
      if (idx === -1) break;

      const angle = angleStep * i + (Math.random() - 0.5) * 0.3;
      const speed = baseSpeed * (0.5 + Math.random() * 0.8);
      const color = paletteColors[Math.floor(Math.random() * paletteColors.length)];

      const p = this.particles[idx];
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.life = 1;
      p.alpha = 1;
      p.size = isSecondary ? 1.5 + Math.random() : 2 + Math.random() * 1.5;
      // Slight color variation per particle for natural look
      p.r = Math.min(255, Math.max(0, color[0] + ((Math.random() - 0.5) * 30) | 0));
      p.g = Math.min(255, Math.max(0, color[1] + ((Math.random() - 0.5) * 30) | 0));
      p.b = Math.min(255, Math.max(0, color[2] + ((Math.random() - 0.5) * 30) | 0));
      p.gravity = gravity;
      p.friction = 0.97;
      p.state = isSecondary ? SPARKLE : BURST;
      p.burstIndex = 0;
    }
  }

  /**
   * Override draw() to use semi-transparent overlay for trail effect
   * instead of the base class's full opaque clearCanvas().
   * Keeps the same frequency mapping + smoothing logic.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    // Ensure buffer is properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    // Map frequency data to element count
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    // Ensure previousAmplitudes is properly sized
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    // Apply smoothing
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Store features for convenience getters (bassEnergy, beatDetected, etc.)
    this._currentFeatures = features;

    // Trail effect: semi-transparent overlay instead of full opaque clear.
    // Old particles dim gradually, creating the trailing afterimage.
    const { ctx, canvas } = this;
    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      ctx.fillStyle = this._trailFillStyle;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    this.renderFrame(amplitudes, features);
  }

  /**
   * Main render: update particle physics, handle launches, draw particles.
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const now = performance.now();

    // --- Beat-triggered launches ---
    const rhythm = features?.rhythm;
    if (rhythm) {
      const { isOnset, confidence } = rhythm;

      if (isOnset && (now - this.lastLaunchTime) > LAUNCH_COOLDOWN_MS) {
        const rocketCount = Math.min(
          config.launchIntensity,
          Math.max(1, Math.ceil(confidence * config.launchIntensity))
        );
        for (let i = 0; i < rocketCount; i++) {
          this.launchRocket(this.bassEnergy);
        }
        this.lastLaunchTime = now;
      }
    }

    // --- Ambient launches for quiet passages ---
    const overallEnergy = (this.bassEnergy + this.midEnergy + this.highEnergy) / 3;
    this.ambientEnergy += overallEnergy * 0.02;
    if (this.ambientEnergy > 0.6) {
      this.launchRocket(overallEnergy);
      this.ambientEnergy = 0;
    }

    // --- Process pending secondary bursts ---
    for (let i = this.pendingBursts.length - 1; i >= 0; i--) {
      const pb = this.pendingBursts[i];
      pb.framesLeft--;
      if (pb.framesLeft <= 0) {
        const count = 20 + Math.floor(Math.random() * 20);
        this._spawnBurst(pb.x, pb.y, pb.paletteColors, count, true);
        this.pendingBursts.splice(i, 1);
      }
    }

    // --- Update and render all particles ---
    const spectralFlux = this.spectralFlux;

    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = this.particles[i];
      if (p.state === DEAD) continue;

      // Physics update
      p.vy += p.gravity;
      p.vx *= p.friction;
      p.vy *= p.friction;
      p.x += p.vx;
      p.y += p.vy;

      // Life decay — rockets burst at target height, others fade out
      if (p.state === ROCKET) {
        p.life -= 0.008;
        if (p.y <= p.targetY || p.vy >= 0 || p.life <= 0) {
          this.burstRocket(i);
          continue;
        }
      } else {
        p.life -= 0.015;
        p.alpha = p.life * p.life; // Quadratic falloff
        p.size *= 0.995;

        if (p.life <= 0 || p.alpha < 0.01) {
          this.freeParticle(i);
          continue;
        }
      }

      // Kill particles far below canvas
      if (p.y > canvas.height + 50) {
        this.freeParticle(i);
        continue;
      }

      // --- Render particle ---
      // Brightness spike from spectral flux on burst particles
      const fluxBoost = p.state === BURST ? 1 + spectralFlux * 0.5 : 1;
      const r = Math.min(255, (p.r * fluxBoost) | 0);
      const g = Math.min(255, (p.g * fluxBoost) | 0);
      const b = Math.min(255, (p.b * fluxBoost) | 0);

      ctx.globalAlpha = p.alpha;

      // Cache fill style string — particles from the same burst share colors,
      // so this avoids most of the ~2000 string allocations per frame
      if (r !== this._lastR || g !== this._lastG || b !== this._lastB) {
        this._lastR = r;
        this._lastG = g;
        this._lastB = b;
        this._lastFillStyle = `rgb(${r},${g},${b})`;
      }

      // Glow only on particles large enough to justify the cost
      const useGlow = config.glow && p.size > 1.5;
      if (useGlow) {
        ctx.shadowBlur = p.size * 4;
        ctx.shadowColor = this._lastFillStyle;
      }

      ctx.fillStyle = this._lastFillStyle;
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(0.5, p.size), 0, Math.PI * 2);
      ctx.fill();

      if (useGlow) {
        ctx.shadowBlur = 0;
      }
    }

    ctx.globalAlpha = 1;
  }

  /**
   * Clear canvas and reset all particle state.
   */
  clear() {
    super.clear();
    this.isFirstFrame = true;

    // Return all particles to pool
    this.freeCount = MAX_PARTICLES;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.particles[i].state = DEAD;
      this.freeList[i] = i;
    }

    this.pendingBursts.length = 0;
    this.ambientEnergy = 0;
    this.lastLaunchTime = 0;
  }

  dispose() {
    super.dispose();
    this.particles = null;
    this.freeList = null;
    this.pendingBursts = null;
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(Fireworks);

export { Fireworks };
export default Fireworks;
